import React, { Fragment, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  ArrowLeftStartOnRectangleIcon,
  IdentificationIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext/ThemeContext';
import { LanguageMenuItems } from './LanguageMenuItems';

import { useTranslation } from 'react-i18next';

const userNavigation = [
  { name: 'Your profile', href: '/profile', icon: IdentificationIcon },
  { name: 'Sign out', href: '/sign-out', icon: ArrowLeftStartOnRectangleIcon },
];

export function ProfileDropDown({
  initials,
}: {
  initials: string;
}): ReactElement {
  const { t } = useTranslation('containerInterface');
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <Menu as="div" className="relative flex-shrink-0">
      <div className="flex h-full items-center">
        <Menu.Button
          className="bg-br-primary/10 focus:ring-br-primary mx-1 h-9 w-9 flex-shrink-0 overflow-hidden rounded-full text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-offset-2 focus:outline-none sm:mx-2"
          data-testid="profile-dropdown"
        >
          {initials.toUpperCase()}
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-0 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none">
          <Menu.Item key="profile">
            {({ active }) => (
              <Link
                to="/profile"
                className={`${active ? 'bg-br-primary/10 text-br-primary dark:text-br-secondary' : 'text-gray-700 dark:text-gray-300'} flex items-center gap-2 px-4 py-2 text-sm`}
                data-testid="profile-dropdown-item-Your profile"
              >
                <IdentificationIcon className="h-4 w-4" />
                {t('yourProfile')}
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={toggleTheme}
                className={`${active ? 'bg-br-primary/10' : ''} flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                data-testid="profile-dropdown-theme-toggle"
              >
                {isDark ? (
                  <SunIcon className="h-4 w-4" />
                ) : (
                  <MoonIcon className="h-4 w-4" />
                )}
                {isDark ? t('lightMode') : t('darkMode')}
              </button>
            )}
          </Menu.Item>
          <LanguageMenuItems />
          <Menu.Item key="sign-out">
            {({ active }) => (
              <Link
                to="/sign-out"
                className={`${active ? 'bg-br-primary/10 text-br-primary dark:text-br-secondary' : 'text-gray-700 dark:text-gray-300'} flex items-center gap-2 px-4 py-2 text-sm`}
                data-testid="profile-dropdown-item-Sign out"
              >
                <ArrowLeftStartOnRectangleIcon className="h-4 w-4" />
                {t('signOut')}
              </Link>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
